import type { CountriesState } from "@/src/reducers/fetch.types";

const MIN_SHADE = 0.25;

const regionNamesByLang = new Map<string, Intl.DisplayNames>();

export interface ValueRange {
  min: number;
  max: number;
}

export function getCountryLabel(code: string, lang: string): string {
  let regionNames = regionNamesByLang.get(lang);
  if (!regionNames) {
    regionNames = new Intl.DisplayNames([lang], {
      type: "region",
      fallback: "code",
    });
    regionNamesByLang.set(lang, regionNames);
  }

  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

export function getValueRange(
  valueByCountry: Readonly<Record<string, number>>,
): ValueRange | null {
  const positiveValueList = Object.values(valueByCountry).filter(
    (value) => value > 0,
  );
  if (!positiveValueList.length) return null;

  return {
    min: positiveValueList.reduce((min, value) => Math.min(min, value)),
    max: positiveValueList.reduce((max, value) => Math.max(max, value)),
  };
}

export function getMapShade(value: number, { min, max }: ValueRange): number {
  if (max <= min) return 1;

  const ratio =
    (Math.log(value) - Math.log(min)) / (Math.log(max) - Math.log(min));
  return MIN_SHADE + (1 - MIN_SHADE) * ratio;
}

export function getCountryCode(
  countries: Pick<CountriesState, "data" | "indexes">,
  uuid: string,
): string | undefined {
  const index = countries.indexes.uuid?.[uuid]?.[0];
  return index === undefined ? undefined : countries.data[index]?.country;
}
